import {useEffect, useRef} from "react";

/**
 * Closes a dialog on the browser/hardware back button instead of leaving the page.
 *
 * While {@code open} is true, one history entry (same URL, marked with a {@code dialogDepth} state flag) is pushed;
 * the back button pops that entry and {@code onClose} is invoked. When the dialog closes any other way, the marker
 * entry is consumed with {@code history.back()} so the stack stays balanced. Entries nest LIFO, so stacked dialogs
 * (e.g. the QR scanner above the location verification) close one per back press.
 *
 * All history operations go through a module-level queue: {@code history.back()} completes asynchronously, so a
 * dialog opening in the same render commit another one closes (verification → open confirmation) must not push its
 * entry until the closing dialog's entry is consumed — otherwise the pending pop would swallow the new entry and
 * instantly close the new dialog.
 *
 * Uses the raw history API on purpose: the URL never changes, and BrowserRouter just sees a same-location POP.
 */
export function useCloseOnBack(open: boolean, onClose: () => void) {
	// The latest close callback without re-running the effect (callers pass fresh closures every render).
	const onCloseRef = useRef(onClose);
	onCloseRef.current = onClose;

	useEffect(() => {
		if (!open) return;

		let active = true;
		let myDepth = 0;
		const handlePopState = () => {
			// Only react when the stack dropped below our own entry — a pop from a dialog stacked above us (or its
			// consuming back()) must not close this one.
			if (myDepth > 0 && dialogDepth() < myDepth) {
				onCloseRef.current();
			}
		};

		enqueue(() => {
			if (!active) return;
			myDepth = dialogDepth() + 1;
			window.history.pushState({...window.history.state, dialogDepth: myDepth}, "");
			window.addEventListener("popstate", handlePopState);
		});

		return () => {
			active = false;
			window.removeEventListener("popstate", handlePopState);
			enqueue(() => {
				// Closed by button/backdrop instead of the back button: our marker is still on the stack — consume it.
				if (myDepth > 0 && dialogDepth() >= myDepth) {
					return popOnce();
				}
			});
		};
	}, [open]);
}

function dialogDepth(): number {
	const depth = (window.history.state as { dialogDepth?: unknown } | null)?.dialogDepth;
	return typeof depth === "number" ? depth : 0;
}

let queue: Promise<unknown> = Promise.resolve();

function enqueue(op: () => Promise<void> | void) {
	queue = queue.then(op, () => undefined);
}

/** Goes back one entry and resolves once the resulting popstate has fired. */
function popOnce(): Promise<void> {
	return new Promise(resolve => {
		const settle = () => {
			window.removeEventListener("popstate", settle);
			resolve();
		};
		window.addEventListener("popstate", settle);
		window.history.back();
	});
}
